import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import appConfig from "../../data/config";
import { apiFetch } from "../../helpers/apiFetch";
import { setWishlistFromApi, addToWishlist, deleteFromWishlist } from "./wishlist-slice";

const authHeader = (token) => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
});

export const fetchWishlist = createAsyncThunk(
    "wishlistApi/fetch",
    async (_, { getState, dispatch }) => {
        const { token } = getState().auth;
        if (!token) return;

        const res = await apiFetch(`${appConfig.apiUrl}/favoritos`, {
            headers: authHeader(token),
        });

        if (res.ok) {
            const data = await res.json();
            dispatch(setWishlistFromApi(data.data.map(f => f.product)));
        }
        // Si falla, el wishlist local queda intacto — no bloquear el boot de la app.
    }
);

/**
 * Migra al servidor los favoritos que el usuario tenía en localStorage de
 * ANTES de que esto tuviera backend (ver docs/plan-favoritos-persistentes.md
 * seccion 3.2/9) — se dispara una sola vez, justo después del login, mismo
 * momento que syncCartOnLogin. Merge, nunca reemplazo: no borra lo que el
 * usuario ya tenga guardado en el servidor de otro dispositivo.
 */
export const syncWishlistOnLogin = createAsyncThunk(
    "wishlistApi/sync",
    async (_, { getState, dispatch }) => {
        const { token } = getState().auth;
        const { wishlistItems } = getState().wishlist;
        if (!token || wishlistItems.length === 0) return;

        const product_codes = wishlistItems.map(item => item.code).filter(Boolean);
        if (product_codes.length === 0) return;

        const res = await apiFetch(`${appConfig.apiUrl}/favoritos/sync`, {
            method: "POST",
            headers: authHeader(token),
            body: JSON.stringify({ product_codes }),
        });

        if (res.ok) {
            const data = await res.json();
            dispatch(setWishlistFromApi(data.data.map(f => f.product)));
        }
    }
);

export const addToWishlistApi = createAsyncThunk(
    "wishlistApi/add",
    async (product, { getState, dispatch }) => {
        dispatch(addToWishlist(product)); // actualización inmediata en UI (toast incluido)

        const { token } = getState().auth;
        if (!token) return;

        try {
            const res = await apiFetch(`${appConfig.apiUrl}/favoritos`, {
                method: "POST",
                headers: authHeader(token),
                body: JSON.stringify({ product_code: product.code }),
            });
            if (!res.ok) {
                dispatch(deleteFromWishlist(product.id));
                toast.error("No se pudo guardar en favoritos");
            }
        } catch {
            dispatch(deleteFromWishlist(product.id));
            toast.error("No se pudo guardar en favoritos");
        }
    }
);

export const removeFromWishlistApi = createAsyncThunk(
    "wishlistApi/remove",
    async (product, { getState, dispatch }) => {
        dispatch(deleteFromWishlist(product.id));

        const { token } = getState().auth;
        if (!token || !product.code) return;

        try {
            const res = await fetch(`${appConfig.apiUrl}/favoritos/${product.code}`, {
                method: "DELETE",
                headers: authHeader(token),
            });
            if (!res.ok && res.status !== 404) {
                toast.error("No se pudo quitar de favoritos");
            }
        } catch {
            toast.error("No se pudo quitar de favoritos");
        }
    }
);

export const clearWishlistApi = createAsyncThunk(
    "wishlistApi/clearAll",
    async (_, { getState, dispatch }) => {
        const { wishlistItems } = getState().wishlist;
        const { token } = getState().auth;
        const codes = wishlistItems.map(item => item.code).filter(Boolean);

        dispatch(setWishlistFromApi([]));

        if (!token) return;

        await Promise.all(codes.map(code =>
            fetch(`${appConfig.apiUrl}/favoritos/${code}`, {
                method: "DELETE",
                headers: authHeader(token),
            }).catch(() => {})
        ));
    }
);
